import React, {useCallback, useEffect, useState} from 'react';
import {useHeader} from "../../contexts/HeaderContext";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import {IconChevronRight, IconMegaphoneFill, IconSpinner} from "../../../components/icons";
import {
  calculateMoyenne,
  formatCurrency,
  getSourceReferencePrefix,
  prepareData,
  prepareMonth,
  sumArrayData,
  roundNumber
} from "../../../components/helpers";
import {useNavigate} from "react-router-dom";
import {Dossier} from "../../../components/models";
import {getStatistic, getStatisticIndicateur} from "../../../components/requests";
import {useAuth} from "../../contexts/AuthContext";
import BarChart from "./components/BarChart";
import DossierStatutList from "./components/DossierStatutList";
import Kpi from "./components/Kpi";
import DoughnutChart from "./components/DoughnutChart";
import {SourceLogo} from "../../../components/common/SourceLogo";
import SwitchButton from "../../../components/common/SwitchButton";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
);

const DashboardPage = () => {
  const {setTitle} = useHeader();
  const {currentUser, isAdmin, isGestionnaire, canView} = useAuth();
  const PUBLIC_URL = process.env.PUBLIC_URL;
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [isDashboardLoading, setIsDasgboardLoading] = useState<boolean>(true);
  const [isStatisticLoading, setIsStatisticLoading] = useState<boolean>(true);
  const [isStatutLoading, setIsStatutLoading] = useState<boolean>(true);
  const [dossierStatut, setDossierStatut] = useState<any | null>(null);
  const [cinqDerniersDossiers, setCinqDerniersDossiers] = useState<Dossier[]>([]);
  const [monthData, setMonthData] = useState<any[]>([]);
  const [vehiculeEnleveTotal, setVehiculeEnleveTotal] = useState<number[]>([]);
  const [vehiculeEnleveTotalLastYear, setVehiculeEnleveTotalLastYear] = useState<number[]>([]);
  const [performanceTauxMoyenne, setPerformanceTauxMoyenne] = useState<number[]>([]);
  const [performanceVHEnleve, setPerformanceVHEnleve] = useState<number>(0);
  const [performanceDelaiMoyenne, setPerformanceDelaiMoyenne] = useState<number>(0);
  const [switchDossierStatut, setSwitchDossierStatut] = useState<string>((new Date()).getFullYear().toString());

  // Méthode pour obtenir les statistiques sur les 5 derniers dossiers
  const getDashboardStatistic = useCallback(() => {
    setIsStatutLoading(true);
    getStatistic(
      (!isAdmin && isGestionnaire) ? currentUser.id : null,
      switchDossierStatut === 'Tous' ? '' : switchDossierStatut
    )
      .then((response) => {
        if (response.status === 200 && response.data !== undefined) {
          setDossierStatut(response.data.dossiers_par_statut);
          setCinqDerniersDossiers(response.data.cinq_derniers_dossiers);
        }
      })
      .catch((error) => console.error('error', error))
      .finally(() => {
        setIsDasgboardLoading(false);
        setIsStatutLoading(false);
      });
  }, [switchDossierStatut, isAdmin, isGestionnaire, currentUser]);

  const getDashboardStatistics = useCallback(() => {
    getStatisticIndicateur((!isAdmin && isGestionnaire) ? currentUser?.id?.toString() : '', '', (new Date())?.getFullYear()?.toString())
      .then((response) => {
        if (response.status === 200 && response.data !== undefined) {
          setMonthData(prepareMonth(response.data?.statisticsLastYear, true));
          setVehiculeEnleveTotal(prepareData(response.data?.statistics, 'totalVehiculeEnleve'));
          setVehiculeEnleveTotalLastYear(prepareData(response.data?.statisticsLastYear, 'totalVehiculeEnleve'));

          setPerformanceTauxMoyenne(prepareData(response.data?.statistics, 'moyenneDelaisOK'));
          setPerformanceVHEnleve(sumArrayData(prepareData(response.data?.statistics, 'performanceVehiculeEnleve')));
          // setPerformanceVHEnCours(sumArrayData(prepareData(response.data?.statistics, 'performanceVehiculeEnCours')));
          setPerformanceDelaiMoyenne(calculateMoyenne(prepareData(response.data?.statistics, 'moyenneJoursRemise')));
        }
      })
      .catch((error) => console.error(error))
      .finally(() => setIsStatisticLoading(false));
  }, [currentUser, isAdmin, isGestionnaire]);

  // useEffect pour définir le titre dans le header
  useEffect(() => {
    setTitle("Accueil");
  }, [setTitle]);

  useEffect(() => {
    getDashboardStatistic();
  }, [getDashboardStatistic]);

  useEffect(() => {
    getDashboardStatistics();
  }, [getDashboardStatistics]);

  const DossierStatut = ({label = ''}: { label?: string }) => {
    let color: string;

    switch (label) {
      case 'Véhicule pris en charge':
        color = '';
        break;
      case 'Enlèvement en cours':
        color = 'bg-green-500';
        break;
      case 'Enlèvement annulé':
        color = 'bg-red-500';
        break;
      default:
        color = '';
    }

    return (
      <div className="flex justify-start items-center gap-2">
        {color && color !== '' && (<div className={`w-4 h-4 ${color} rounded-full`}/>)}
        <p className="text-sm font-medium text-gray-500">
          {label}
        </p>
      </div>
    );
  }

  if (isDashboardLoading || isStatisticLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen-fit">
        <IconSpinner className="animate-spin w-10 h-10"/>
      </div>
    );
  }

  return (
    <div className="min-h-screen animate-in fade-in duration-300 bg-gray-50 pb-24 md:pb-24">
      <div className="pt-4">
        <div
          className="p-4 w-full relative text-gray-800 bg-white shadow-md"
          style={{
            backgroundImage: `linear-gradient(to right, white 25%, rgba(255,255,255,0) 100%), url('${PUBLIC_URL}/camion.webp')`,
            backgroundSize: 'cover',
            backgroundPosition: '50% 35%',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="w-1/3 pb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Bienvenue sur votre espace partenaire
            </h3>
            <p className="text-sm text-gray-500">
              Suivi des enlèvements, pilotage de la performance et gestion des dossiers.
            </p>
          </div>

          {canView('Tableau de bord') && (
            <div className="rounded-xl border-l border-b shadow border-gray-100 w-2/5 p-4">
              <h3 className="font-bold text-gray-900 mb-4">Synthèse de performance sur {currentYear}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Kpi
                  value={performanceVHEnleve?.toString()}
                  label="Véhicules enlevés"
                  color="text-red-500"
                  description="Nombre de véhicules enlevés : cumul"
                />
                <Kpi
                  value={`${performanceDelaiMoyenne} j`}
                  label="Délai moyen d'enlèvement"
                  description="Délai moyen d'enlèvement en jours calendaires"
                />
                <DoughnutChart
                  value={Number(roundNumber((sumArrayData(performanceTauxMoyenne) / sumArrayData(vehiculeEnleveTotal)) * 100))}
                  description="Taux de service : Part des dossiers enlevés dans les délais"
                />
              </div>
            </div>
          )}
        </div>

        <div className="p-4 space-y-4">
          <div className="flex flex-col lg:flex-row lg:space-x-4">
            {canView('Dossier') && (
              <div className="relative rounded-xl flex-1 lg:w-3/5 bg-white shadow-md border border-gray-100 z-10">
                <div className="flex justify-between p-4">
                  <div>
                    <h3 className="font-bold text-gray-900">Voir les dossiers</h3>
                    <SwitchButton
                      leftValue={'Tous'}
                      rightValue={(new Date()).getFullYear().toString()}
                      switchValue={switchDossierStatut}
                      setSwitchValue={setSwitchDossierStatut}
                    />
                  </div>
                  <button
                    onClick={() => navigate('/dossiers', {
                      state: {isSecondaire: false, currentPage: 1},
                      replace: true
                    })}
                    className="flex justify-center items-start gap-1 text-emerald-600 hover:text-emerald-700 text-sm font-medium transition-colors"
                  >
                    Voir tout
                    <IconChevronRight className="w-4 h-4 mt-1"/>
                  </button>
                </div>
                {(isStatutLoading) ? (
                  <div className="flex justify-center items-center min-h-[190px]">
                    <IconSpinner className="animate-spin w-10 h-10"/>
                  </div>
                ) : (
                  <DossierStatutList displayMax={9} dossierStatut={dossierStatut}/>
                )}
              </div>
            )}

            {canView('Tableau de bord') && (
              <div className="rounded-xl w-full lg:w-2/5 bg-white shadow-md border border-gray-100">
                <div className="flex justify-between p-4">
                  <h3 className="font-bold text-gray-900">Indicateurs de suivi sur {currentYear}</h3>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="flex justify-center items-start gap-1 text-emerald-600 hover:text-emerald-700 text-sm font-medium transition-colors"
                  >
                    Voir tout
                    <IconChevronRight className="w-4 h-4 mt-1"/>
                  </button>
                </div>
                <div className="text-center">
                  <h3 className="font-bold text-sm text-gray-900">Nombre de véhicules enlevés</h3>
                </div>
                <div className="flex p-3 gap-4">
                  <BarChart
                    month={monthData}
                    total={[vehiculeEnleveTotalLastYear, vehiculeEnleveTotal]}
                    year={Number(new Date()?.getFullYear())}
                    thickness={15}
                    displayLegend={true}
                    fontSize={10}
                    height={180}
                  />
                </div>
              </div>
            )}
          </div>

          {canView('Dossier') && (
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
              <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-gray-900">Derniers dossiers</h3>
                </div>
                <button
                  onClick={() => navigate('/dossiers', {
                    state: {isSecondaire: false, currentPage: 1},
                    replace: true
                  })}
                  className="flex justify-between items-center gap-1 text-emerald-600 hover:text-emerald-700 text-sm font-medium transition-colors"
                >
                  Voir tout
                  <IconChevronRight className="w-4 h-4 mt-1"/>
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-5 py-3 text-left font-semibold">Source</th>
                    <th className="px-5 py-3 text-left font-semibold">Dossier</th>
                    <th className="px-5 py-3 text-left font-semibold">Véhicule</th>
                    <th className="px-5 py-3 text-left font-semibold">Statut</th>
                    <th className="px-5 py-3 text-left font-semibold">Montant</th>
                    {canView('Dossier') && <th className="px-5 py-3 text-left font-semibold">Action</th>}
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                  {(cinqDerniersDossiers.length === 0) ? (
                    <tr>
                      <td colSpan={12}>
                        <div className="flex items-center justify-center min-h-[300px]">
                          <p className="text-gray-500">Pas de résultat</p>
                        </div>
                      </td>
                    </tr>
                  ) : cinqDerniersDossiers?.map((dossier) => (
                    <tr
                      key={dossier?.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/dossiers/gerer/${dossier?.id}`, {state: {dossierData: dossier}})}
                    >
                      <td className="px-5">
                        <p className="text-gray-500 font-mono">
                          <SourceLogo source={dossier?.source?.libelle} classExtra='w-10 h-10'/>
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="font-medium text-gray-900">
                          {getSourceReferencePrefix(dossier?.source?.libelle)}
                          {dossier?.reference}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="font-medium text-gray-900">
                          {dossier?.vehicule?.marque} / {dossier?.vehicule?.modele}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <DossierStatut label={dossier?.statut?.libelle}/>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <p
                          className="font-medium text-gray-900">{formatCurrency(dossier?.frais?.[0]?.fraisTotauxTTC || 0)}</p>
                      </td>
                      {canView('Dossier') &&
                        <td className="px-5 py-4">
                          <button
                            className="bg-emerald-600 text-white px-4 py-1 rounded hover:bg-emerald-700 text-sm font-medium transition-colors"
                          >
                            Afficher
                          </button>
                        </td>}
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <footer
        className="fixed bottom-0 w-full z-50 bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] border-t border-gray-100">
        <div className="flex gap-4">
          <div className="w-1/6 flex justify-evenly items-center gap-4 flex-shrink-0">
            <IconMegaphoneFill className="w-10 h-10 text-sky-600"/>
            <div className="w-12 h-12">
              <a
                href="https://www.groupesurplus.com/fr/actualites"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={`${PUBLIC_URL}/favicon.ico`} alt="logo"/>
              </a>
            </div>
          </div>
          <p className="text-sm text-gray-500 w-5/6 border-s border-gray-100 p-4">
            <a
              href="https://www.groupesurplus.com/fr/actualites"
              target="_blank"
              rel="noopener noreferrer"
            >
              <b>Découvrez les dernières nouveautés, annonces et temps forts du Groupe Surplus Recyclage.</b> <br/>
              Un espace dédié pour ne rien manquer.
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DashboardPage;